import {useMemo} from 'react'
import {useProducts} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

import {usesSfccShopTheLook} from '../../../../../utils/site-config'
import {isShopTheLookSource} from './helpers'

const RECOMMENDATION_TYPE = 'Product Detail Page - Cross Sell'
/* SCAPI getProducts caps `ids` at 24 per call. */
const SCAPI_GET_PRODUCTS_MAX_IDS = 24
const STALE_TIME = 5 * 60 * 1000

const getCrossSellIds = (product) =>
    (product?.recommendations || [])
        .filter((item) => item?.recommendationType?.displayValue === RECOMMENDATION_TYPE)
        .map((item) => item?.recommendedItemId)
        .filter(Boolean)

/* Shop The Look served from the cross-sells already on the PDP product document,
 * when `vickyShopTheLookSource` selects SFCC over VickyAI. Reads the same config
 * value that gates `expand=recommendations` on that fetch, so the two can't disagree. */
export const useSfccRecommendationTiles = (dynamicContent, product) => {
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const isActive = isShopTheLookSource(dynamicContent) && usesSfccShopTheLook(appConfig, site?.id)

    const recommendationIds = useMemo(() => {
        if (!isActive) return ''
        return getCrossSellIds(product).slice(0, SCAPI_GET_PRODUCTS_MAX_IDS).join(',')
    }, [isActive, product])

    const {data: productsData, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: recommendationIds,
                allImages: true,
                perPricebook: true,
                expand: ['images', 'variations', 'prices', 'promotions']
            }
        },
        {
            enabled: isActive && Boolean(recommendationIds),
            keepPreviousData: true,
            staleTime: STALE_TIME
        }
    )

    /* SCAPI returns the products unsorted vs the requested ids; re-order so the
     * carousel preserves the order the recommendations were authored in. */
    const tiles = useMemo(() => {
        if (!recommendationIds) return []
        const byId = new Map((productsData?.data || []).map((p) => [p?.id, p]).filter(([id]) => id))
        return recommendationIds
            .split(',')
            .map((id) => byId.get(id))
            .filter(Boolean)
            .map((recommended) => ({
                __typename: 'GDIProductTileBlock',
                Product: recommended,
                ContentLink: {GuidValue: `sfcc-rec-${recommended.id}`}
            }))
    }, [recommendationIds, productsData])

    if (!isActive) return {tiles: [], isLoading: false, isActive: false}

    return {
        tiles,
        isLoading: Boolean(recommendationIds) && isProductsLoading,
        isActive: true
    }
}
